import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ChevronLeft, ChevronRight, PieChart as PieIcon, Filter, Sparkles } from 'lucide-react';
import { fmtMoney } from '../lib/format';
import { RECHARTS_TOOLTIP_STYLE } from '../lib/animations';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useCategoryColors } from '../contexts/CategoryColorsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatMonth = (yyyymm) => {
  if (!yyyymm) return '';
  const [y, m] = yyyymm.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};

const shiftMonth = (yyyymm, delta) => {
  const [y, m] = yyyymm.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const newY = Math.floor(total / 12);
  const newM = (total % 12) + 1;
  return `${newY.toString().padStart(4, '0')}-${newM.toString().padStart(2, '0')}`;
};

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
};

const compareMonth = (a, b) => {
  // returns -1 / 0 / 1 (numeric YYYY-MM ordering)
  const [ay, am] = a.split('-').map(Number);
  const [by, bm] = b.split('-').map(Number);
  const av = ay * 12 + am;
  const bv = by * 12 + bm;
  return av < bv ? -1 : av > bv ? 1 : 0;
};

const SpendingByCategoryWidget = () => {
  const [month, setMonth] = useState(currentMonth());
  const [todayMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Full universe of distinct expense categories for this user (from /expense-categories endpoint).
  const [allCategories, setAllCategories] = useState([]);
  // Selected categories — undefined means "all" (no whitelist sent to backend);
  // an array (even empty) means an explicit selection has been applied.
  const [selectedCategories, setSelectedCategories] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  // Account scope: 'all' (asset accounts only, server-side liability exclusion) or a specific account_id.
  const [accountId, setAccountId] = useState('all');
  const [accounts, setAccounts] = useState([]);
  // User-chosen category colours (from the global picker context). These take priority
  // over any backend palette fallback so the donut stays in sync with the Transactions
  // table / Recurring cards swatches the user has personally set.
  const { getColor } = useCategoryColors();

  // Load accounts once on mount — used by the account scope dropdown.
  useEffect(() => {
    let cancelled = false;
    const loadAccounts = async () => {
      try {
        const r = await axios.get(`${API}/accounts`, { withCredentials: true });
        if (!cancelled) setAccounts(r.data || []);
      } catch {
        if (!cancelled) setAccounts([]);
      }
    };
    loadAccounts();
    return () => { cancelled = true; };
  }, []);

  // Load categories whenever account scope changes — the universe of expense
  // categories differs per-account (a Loan account only has "Loan Repayment" etc).
  useEffect(() => {
    let cancelled = false;
    const loadCats = async () => {
      try {
        const params = new URLSearchParams();
        if (accountId !== 'all') params.set('account_id', accountId);
        const qs = params.toString();
        const url = `${API}/transactions/expense-categories${qs ? `?${qs}` : ''}`;
        const r = await axios.get(url, { withCredentials: true });
        if (!cancelled) {
          setAllCategories(r.data.categories || []);
          // Reset filter to "all" when scope changes so users see everything on the new scope.
          setSelectedCategories(null);
        }
      } catch {
        if (!cancelled) setAllCategories([]);
      }
    };
    loadCats();
    return () => { cancelled = true; };
  }, [accountId]);

  // Refetch the breakdown when month, account scope, or category filter changes.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!cancelled) setLoading(true);
      try {
        const params = new URLSearchParams({ month });
        if (selectedCategories !== null) {
          params.set('categories', selectedCategories.join(','));
        }
        if (accountId !== 'all') params.set('account_id', accountId);
        const res = await axios.get(`${API}/transactions/spending-by-category?${params.toString()}`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ month, total_spent: 0, transaction_count: 0, breakdown: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [month, selectedCategories, accountId]);

  const breakdown = useMemo(() => {
    // Overlay any user-picked colour on top of the backend palette so the donut
    // matches the swatches users see in Transactions/Recurring.
    const raw = data?.breakdown || [];
    return raw.map((b) => {
      const override = getColor(b.category);
      return override ? { ...b, color: override } : b;
    });
  }, [data, getColor]);
  const totalSpent = data?.total_spent || 0;
  const isFuture = compareMonth(month, todayMonth) > 0;

  const chartData = useMemo(() => breakdown.map((b) => ({
    name: b.category,
    value: b.total,
    color: b.color,
    percentage: b.percentage,
  })), [breakdown]);

  const activeSelectionLabel = selectedCategories === null
    ? `All categories (${allCategories.length})`
    : selectedCategories.length === 0
      ? 'No categories selected'
      : selectedCategories.length === allCategories.length
        ? `All categories (${allCategories.length})`
        : `${selectedCategories.length} of ${allCategories.length} selected`;

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) => {
      const base = prev === null ? [...allCategories] : [...prev];
      const idx = base.indexOf(cat);
      if (idx >= 0) base.splice(idx, 1);
      else base.push(cat);
      return base;
    });
  };

  const selectAll = () => setSelectedCategories(null);
  const deselectAll = () => setSelectedCategories([]);

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="spending-by-category-widget" data-pdf-keep-together="true">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <PieIcon className="w-5 h-5 text-[#0052FF]" />
          <h2 className="text-2xl tracking-tight font-bold text-[#020617]">Spending by Category</h2>
        </div>
        <div className="flex items-center gap-2 print-hide flex-wrap">
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger className="w-[180px] text-xs h-8" data-testid="spending-account-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="spending-account-option-all">All Accounts (assets)</SelectItem>
              {accounts.map((acc) => (
                <SelectItem key={acc.account_id} value={acc.account_id} data-testid={`spending-account-option-${acc.account_id}`}>
                  {acc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs font-medium"
                data-testid="spending-category-filter-trigger"
              >
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                {activeSelectionLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="end" data-testid="spending-category-filter-popover">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E8F0]">
                <span className="text-xs uppercase tracking-[0.14em] font-bold text-[#020617]">Expense categories</span>
                <div className="flex gap-1">
                  <button type="button" onClick={selectAll} className="text-[10px] font-semibold text-[#0052FF] hover:underline" data-testid="spending-category-filter-select-all">All</button>
                  <span className="text-[10px] text-[#94A3B8]">|</span>
                  <button type="button" onClick={deselectAll} className="text-[10px] font-semibold text-[#64748B] hover:underline" data-testid="spending-category-filter-deselect-all">None</button>
                </div>
              </div>
              {allCategories.length === 0 ? (
                <div className="px-3 py-4 text-xs text-[#64748B]">No expense categories yet. Tag transactions to see them here.</div>
              ) : (
                <div className="max-h-[260px] overflow-y-auto py-1">
                  {allCategories.map((c) => {
                    const checked = selectedCategories === null ? true : selectedCategories.includes(c);
                    return (
                      <label
                        key={c}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#334155] hover:bg-[#F8FAFC] cursor-pointer"
                        data-testid={`spending-category-filter-item-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleCategory(c)} />
                        <span className="truncate">{c}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </PopoverContent>
          </Popover>

          <button
            type="button"
            onClick={() => setMonth(shiftMonth(month, -1))}
            className="p-1.5 rounded-md border border-[#E2E8F0] text-[#64748B] hover:text-[#0052FF] hover:border-[#0052FF] transition-colors"
            aria-label="Previous month"
            data-testid="spending-prev-month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-sm font-medium text-[#020617] min-w-[120px] text-center" data-testid="spending-current-month">
            {formatMonth(month)}
          </div>
          <button
            type="button"
            onClick={() => setMonth(shiftMonth(month, 1))}
            className="p-1.5 rounded-md border border-[#E2E8F0] text-[#64748B] hover:text-[#0052FF] hover:border-[#0052FF] transition-colors"
            aria-label="Next month"
            data-testid="spending-next-month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isFuture && (
        <div
          className="mb-4 flex items-start gap-2 px-3 py-2 rounded-md bg-[#FFFBEB] border border-[#FDE68A] text-[12px] text-[#92400E]"
          data-testid="spending-forecast-banner"
        >
          <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Forecast view.</strong> Future months show projected spending based on your scheduled recurring transactions and any future-dated one-offs you&apos;ve added.
          </span>
        </div>
      )}

      {loading ? (
        <div className="h-[280px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]"></div>
        </div>
      ) : breakdown.length === 0 ? (
        <div className="h-[200px] flex flex-col items-center justify-center text-center" data-testid="spending-empty-state">
          <p className="text-sm text-[#64748B]">
            {isFuture
              ? `No projected spending in ${formatMonth(month)}.`
              : `No expense transactions in ${formatMonth(month)}.`}
          </p>
          <p className="text-xs text-[#94A3B8] mt-1">
            {isFuture
              ? 'Add recurring transactions or future-dated expenses to see a forecast here.'
              : 'Add transactions with categories to see your breakdown here.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="relative" data-testid="spending-donut-chart">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={2}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  isAnimationActive={false}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={RECHARTS_TOOLTIP_STYLE}
                  formatter={(value, name, props) => [
                    `£${fmtMoney(value)} (${props.payload.percentage}%)`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-2" data-testid="spending-donut-center">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.14em] font-semibold text-[#64748B] text-center max-w-[120px] truncate">{isFuture ? 'Projected' : 'Total spent'}</div>
              <div className="text-2xl font-bold text-[#020617] mt-1">£{fmtMoney(totalSpent)}</div>
              <div className="text-[11px] text-[#64748B] mt-0.5">{data?.transaction_count || 0} transaction{data?.transaction_count === 1 ? '' : 's'}</div>
            </div>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="spending-legend">
            {breakdown.map((b, idx) => (
              <div key={b.category} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-[#F8FAFC] transition-colors" data-testid={`spending-legend-item-${idx}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: b.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#020617] truncate" title={b.category}>{b.category}</div>
                    <div className="text-xs text-[#64748B]">{b.count} transaction{b.count === 1 ? '' : 's'} · {b.percentage}%</div>
                  </div>
                </div>
                <div className="text-sm font-semibold text-[#020617] whitespace-nowrap">
                  £{fmtMoney(b.total)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpendingByCategoryWidget;
